import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Notice the confusion",
    opening: localizedGenderNode(
      [
      "Today I don't try to define who I am.",
      "I just sit with myself inside the question.",
      "That is enough for this moment."
    ],
      [
      "Today I don't try to define who I am.",
      "I just sit with myself inside the question.",
      "That is enough for this moment."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath."
    ],
      [
      "I pause with myself.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "When do I feel least like myself?",
      "What happened before I started feeling this way?",
      "Which part of me is asking for recognition now?"
    ],
      [
      "When do I feel least like myself?",
      "What happened before I started feeling this way?",
      "Which part of me is asking for recognition now?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Even confusion is a sign that I am still looking for me."
    ],
      [
      "Even confusion is a sign that I am still looking for me."
    ]
    ),
    action: localizedGenderNode(
      "one sentence to myself — \"I am here\".",
      "one sentence to myself — \"I am here\"."
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "What changed in me",
    opening: localizedGenderNode(
      [
      "Today I notice the changes.",
      "Without trying to fix them.",
      "Just to see."
    ],
      [
      "Today I notice the changes.",
      "Without trying to fix them.",
      "Just to see."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I feel my body on the chair."
    ],
      [
      "I breathe slowly.",
      "I feel my body on the chair."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What in me has changed in this period?",
      "What has stayed the same despite everything?",
      "What scares me about this change?"
    ],
      [
      "What in me has changed in this period?",
      "What has stayed the same despite everything?",
      "What scares me about this change?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Change doesn't erase me, it just confuses me."
    ],
      [
      "Change doesn't erase me, it just confuses me."
    ]
    ),
    action: localizedGenderNode(
      "one breath with the word \"allowed\".",
      "one breath with the word \"allowed\"."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "Moments when I am me",
    opening: localizedGenderNode(
      [
      "Today I look for moments of me.",
      "Even if they are small.",
      "That is enough."
    ],
      [
      "Today I look for moments of me.",
      "Even if they are small.",
      "That is enough."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I notice my breath."
    ],
      [
      "I pause for a moment.",
      "I notice my breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "When did I last feel like myself?",
      "What was there that I liked?",
      "How does it feel in my body when I am me?"
    ],
      [
      "When did I last feel like myself?",
      "What was there that I liked?",
      "How does it feel in my body when I am me?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "There are points of light in me, even if they are rare."
    ],
      [
      "There are points of light in me, even if they are rare."
    ]
    ),
    action: localizedGenderNode(
      "remember one such moment.",
      "remember one such moment."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Roles that fell away",
    opening: localizedGenderNode(
      [
      "Today I see the roles that no longer hold.",
      "Without blame.",
      "Just truth."
    ],
      [
      "Today I see the roles that no longer hold.",
      "Without blame.",
      "Just truth."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I place a hand on my heart.",
      "One breath."
    ],
      [
      "I place a hand on my heart.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Which role in me fell away recently?",
      "What did it give me that felt safe?",
      "What am I afraid to be without it?"
    ],
      [
      "Which role in me fell away recently?",
      "What did it give me that felt safe?",
      "What am I afraid to be without it?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Even without a role, I am still me."
    ],
      [
      "Even without a role, I am still me."
    ]
    ),
    action: localizedGenderNode(
      "one sentence of comfort to myself.",
      "one sentence of comfort to myself."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "What I am trying to be",
    opening: localizedGenderNode(
      [
      "Today I notice what I am trying to hold.",
      "Not to judge.",
      "Just to see."
    ],
      [
      "Today I notice what I am trying to hold.",
      "Not to judge.",
      "Just to see."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe into my belly.",
      "I am here."
    ],
      [
      "I breathe into my belly.",
      "I am here."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What am I trying to be to feel safe?",
      "Who am I trying to fit for?",
      "What does this cost me?"
    ],
      [
      "What am I trying to be to feel safe?",
      "Who am I trying to fit for?",
      "What does this cost me?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Holding a role is tiring."
    ],
      [
      "Holding a role is tiring."
    ]
    ),
    action: localizedGenderNode(
      "relax the effort for one moment.",
      "relax the effort for one moment."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Voices from outside",
    opening: localizedGenderNode(
      [
      "Today I identify what comes from outside.",
      "And what stays inside.",
      "Without a fight."
    ],
      [
      "Today I identify what comes from outside.",
      "And what stays inside.",
      "Without a fight."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I listen to the quiet."
    ],
      [
      "I pause for a moment.",
      "I listen to the quiet."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Which outside voice rules me most?",
      "What does it tell me I \"should\" be?",
      "What does my inner voice say alongside it?"
    ],
      [
      "Which outside voice rules me most?",
      "What does it tell me I \"should\" be?",
      "What does my inner voice say alongside it?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "There is more than one voice in me, and I can choose who to listen to."
    ],
      [
      "There is more than one voice in me, and I can choose who to listen to."
    ]
    ),
    action: localizedGenderNode(
      "make room for one quiet voice.",
      "make room for one quiet voice."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "What I miss in myself",
    opening: localizedGenderNode(
      [
      "Today I meet an inner longing.",
      "Not to fix.",
      "Just to feel."
    ],
      [
      "Today I meet an inner longing.",
      "Not to fix.",
      "Just to feel."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I place a hand on my belly.",
      "One breath."
    ],
      [
      "I place a hand on my belly.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What do I miss in myself?",
      "When did I have more of it?",
      "What would give it a tiny place today?"
    ],
      [
      "What do I miss in myself?",
      "When did I have more of it?",
      "What would give it a tiny place today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Longing is also a thread of connection."
    ],
      [
      "Longing is also a thread of connection."
    ]
    ),
    action: localizedGenderNode(
      "remind myself of one thing I like in me.",
      "remind myself of one thing I like in me."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Quiet values",
    opening: localizedGenderNode(
      [
      "Today I look at what truly matters to me.",
      "Not loudly.",
      "Quietly."
    ],
      [
      "Today I look at what truly matters to me.",
      "Not loudly.",
      "Quietly."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I give myself a moment."
    ],
      [
      "I breathe slowly.",
      "I give myself a moment."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Which one value stays with me?",
      "How does it look on a simple day?",
      "What can I do to honor it today?"
    ],
      [
      "Which one value stays with me?",
      "How does it look on a simple day?",
      "What can I do to honor it today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "One value can be a thread of me."
    ],
      [
      "One value can be a thread of me."
    ]
    ),
    action: localizedGenderNode(
      "one word that expresses the value.",
      "one word that expresses the value."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "A small boundary",
    opening: localizedGenderNode(
      [
      "Today I check a boundary that protects me.",
      "Not to distance.",
      "To stay."
    ],
      [
      "Today I check a boundary that protects me.",
      "Not to distance.",
      "To stay."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I feel my feet on the floor."
    ],
      [
      "I pause for a moment.",
      "I feel my feet on the floor."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Where do I tend to give myself up?",
      "Which small boundary would help me stay in me?",
      "How can I say it to myself first?"
    ],
      [
      "Where do I tend to give myself up?",
      "Which small boundary would help me stay in me?",
      "How can I say it to myself first?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "A small boundary is protection, not a fight."
    ],
      [
      "A small boundary is protection, not a fight."
    ]
    ),
    action: localizedGenderNode(
      "one boundary sentence to myself.",
      "one boundary sentence to myself."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "A small choice",
    opening: localizedGenderNode(
      [
      "Today I choose something small that brings me closer to me.",
      "No drama.",
      "A small step."
    ],
      [
      "Today I choose something small that brings me closer to me.",
      "No drama.",
      "A small step."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe and notice.",
      "I am here."
    ],
      [
      "I breathe and notice.",
      "I am here."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What small choice would feel true to me?",
      "What stops me from doing it?",
      "What happens if I do only 10% of it?"
    ],
      [
      "What small choice would feel true to me?",
      "What stops me from doing it?",
      "What happens if I do only 10% of it?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "A small choice can remind me who I am."
    ],
      [
      "A small choice can remind me who I am."
    ]
    ),
    action: localizedGenderNode(
      "one 10% step.",
      "one 10% step."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "A new name for myself",
    opening: localizedGenderNode(
      [
      "Today I give myself a gentle description.",
      "Not a label.",
      "Just kind words."
    ],
      [
      "Today I give myself a gentle description.",
      "Not a label.",
      "Just kind words."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "I feel the air come in and out."
    ],
      [
      "I pause for a moment.",
      "I feel the air come in and out."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What good word describes me today?",
      "What do I do that shows this word?",
      "How does it feel to hear it about me?"
    ],
      [
      "What good word describes me today?",
      "What do I do that shows this word?",
      "How does it feel to hear it about me?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "One good word can be an anchor."
    ],
      [
      "One good word can be an anchor."
    ]
    ),
    action: localizedGenderNode(
      "say that word in my heart.",
      "say that word in my heart."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Threads that hold",
    opening: localizedGenderNode(
      [
      "Today I notice threads that hold me.",
      "Even thin ones.",
      "They exist."
    ],
      [
      "Today I notice threads that hold me.",
      "Even thin ones.",
      "They exist."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe slowly.",
      "I feel my body soften a little."
    ],
      [
      "I breathe slowly.",
      "I feel my body soften a little."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What holds me when I feel confused?",
      "Who or what reminds me who I am?",
      "What can I hold today?"
    ],
      [
      "What holds me when I feel confused?",
      "Who or what reminds me who I am?",
      "What can I hold today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "One thin thread is enough to not lose everything."
    ],
      [
      "One thin thread is enough to not lose everything."
    ]
    ),
    action: localizedGenderNode(
      "hold something small that calms me.",
      "hold something small that calms me."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "Being in process",
    opening: localizedGenderNode(
      [
      "Today I allow myself to be in-between.",
      "Not final.",
      "Not a mistake."
    ],
      [
      "Today I allow myself to be in-between.",
      "Not final.",
      "Not a mistake."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause for a moment.",
      "One breath."
    ],
      [
      "I pause for a moment.",
      "One breath."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Where do I demand an answer from myself right now?",
      "What happens if I give myself time?",
      "What does one day look like without chasing an answer?"
    ],
      [
      "Where do I demand an answer from myself right now?",
      "What happens if I give myself time?",
      "What does one day look like without chasing an answer?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Being in-between is a real place."
    ],
      [
      "Being in-between is a real place."
    ]
    ),
    action: localizedGenderNode(
      "tell myself \"It's okay not to know\".",
      "tell myself \"It's okay not to know\"."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Return sentence",
    opening: localizedGenderNode(
      [
      "Today I choose a sentence that brings me back.",
      "When I get lost.",
      "A small sentence."
    ],
      [
      "Today I choose a sentence that brings me back.",
      "When I get lost.",
      "A small sentence."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I breathe.",
      "I am here."
    ],
      [
      "I breathe.",
      "I am here."
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What did I learn about myself in this journey?",
      "What is the first sign I am drifting from myself?",
      "Which short sentence brings me back?"
    ],
      [
      "What did I learn about myself in this journey?",
      "What is the first sign I am drifting from myself?",
      "Which short sentence brings me back?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "I can get lost and bring myself back with one breath."
    ],
      [
      "I can get lost and bring myself back with one breath."
    ]
    ),
    action: localizedGenderNode(
      "say the new sentence once.",
      "say the new sentence once."
    ),
  },
].map(buildNormalizedDay);

export const lossOfSenseOfIdentityWhoAmIEvenTodayBlueprintEn = buildNormalizedPilotBlueprint({
  id: "loss_of_sense_of_identity_who_am_i_even_today_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "loss_of_sense_of_identity_who_am_i_even_today",
  category: "depression_emptiness_meaning",
  journeyName: {
    he: "אובדן תחושת זהות (מי אני בכלל היום)",
    en: "Loss of sense of identity (Who am I even today)",
    es: "Pérdida del sentido de identidad (¿Quién soy yo hoy?)",
  },
  days,
});

export default lossOfSenseOfIdentityWhoAmIEvenTodayBlueprintEn;
